/**
 * Internationalized Formatting Utilities
 *
 * Provides locale-aware formatting functions for dates, times, numbers, and currency.
 * All formatters respect the user's currently selected language from i18next.
 *
 * @module i18n/formatters
 */

import i18n from './config';

/**
 * Get the current locale from i18n
 * Falls back to 'de' if no language is set
 */
const getCurrentLocale = (): string => {
  return i18n.language || 'de';
};

/**
 * Format a date according to the user's locale
 *
 * @param date - The date to format (string or Date object)
 * @param format - The format style: 'short' (date only) or 'long' (date + time)
 * @returns Formatted date string
 *
 * @example
 * // German locale
 * formatDate(new Date('2024-01-15'), 'short') // "15.01.2024"
 * formatDate(new Date('2024-01-15'), 'long')  // "15.01.2024, 14:30"
 *
 * // English locale
 * formatDate(new Date('2024-01-15'), 'short') // "1/15/2024"
 * formatDate(new Date('2024-01-15'), 'long')  // "1/15/2024, 2:30 PM"
 */
export function formatDate(
  date: string | Date,
  format: 'short' | 'long' = 'short'
): string {
  const locale = getCurrentLocale();
  const dateObj = new Date(date);

  if (format === 'short') {
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(dateObj);
  }

  // 'long' format includes time
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(dateObj);
}

/**
 * Format a date and time according to the user's locale
 *
 * @param date - The date to format (string or Date object)
 * @returns Formatted date and time string
 *
 * @example
 * // German locale: "15.01.2024, 14:30"
 * // English locale: "1/15/2024, 2:30 PM"
 */
export function formatDateTime(date: string | Date): string {
  return formatDate(date, 'long');
}

/**
 * Format only the time part of a date, according to the user's locale.
 *
 * Needed where date and time are shown on separate lines — formatDate('long')
 * always renders both.
 *
 * @example
 * // German locale: "18:00"
 * // English locale: "6:00 PM"
 */
export function formatTime(date: string | Date): string {
  return new Intl.DateTimeFormat(getCurrentLocale(), {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date));
}

/**
 * Format a relative time string according to the user's locale
 *
 * @param date - The date to format relative to now
 * @returns Localized relative time string
 *
 * @example
 * // German locale
 * formatRelativeTime(twoHoursAgo) // "vor 2 Stunden"
 * formatRelativeTime(threeDaysAgo) // "vor 3 Tagen"
 *
 * // English locale
 * formatRelativeTime(twoHoursAgo) // "2 hours ago"
 * formatRelativeTime(threeDaysAgo) // "3 days ago"
 */
export function formatRelativeTime(date: string | Date): string {
  const locale = getCurrentLocale();
  const now = new Date();
  const then = new Date(date);
  const diffInSeconds = Math.floor((now.getTime() - then.getTime()) / 1000);

  // For very recent times (< 1 minute), use "just now"
  if (diffInSeconds < 60) {
    // Could be localized further, but "just now" works for now
    return locale === 'de' ? 'gerade eben' : 'just now';
  }

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'long' });

  // Minutes
  if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return rtf.format(-minutes, 'minute');
  }

  // Hours
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return rtf.format(-hours, 'hour');
  }

  // Days
  if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return rtf.format(-days, 'day');
  }

  // For older dates, show the formatted date
  return formatDate(date, 'short');
}

/**
 * Format a currency amount according to the user's locale
 *
 * @param amount - The numeric amount to format
 * @param currency - The currency code (default: 'EUR')
 * @returns Formatted currency string
 *
 * @example
 * // German locale
 * formatCurrency(1234.56) // "1.234,56 €"
 * formatCurrency(1234.56, 'USD') // "1.234,56 $"
 *
 * // English locale
 * formatCurrency(1234.56) // "€1,234.56"
 * formatCurrency(1234.56, 'USD') // "$1,234.56"
 */
export function formatCurrency(amount: number, currency: string = 'EUR'): string {
  const locale = getCurrentLocale();

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(amount);
}

/**
 * Format a number according to the user's locale
 *
 * @param num - The number to format
 * @param options - Optional formatting options (decimals, etc.)
 * @returns Formatted number string
 *
 * @example
 * // German locale
 * formatNumber(1234.56) // "1.234,56"
 * formatNumber(1234.56, { maximumFractionDigits: 0 }) // "1.235"
 *
 * // English locale
 * formatNumber(1234.56) // "1,234.56"
 * formatNumber(1234.56, { maximumFractionDigits: 0 }) // "1,235"
 */
export function formatNumber(
  num: number,
  options?: Intl.NumberFormatOptions
): string {
  const locale = getCurrentLocale();

  return new Intl.NumberFormat(locale, options).format(num);
}
